
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ProjectAssetMappingService } from '@app/cem/project/content/project-asset-mapping/project-asset-mapping.service';
import { ErrorService } from '@app/common/services/error.service';
import { ProjectTypeId } from '@app/shared/data/project-enums';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { NumericTextBoxComponent, TextBoxComponent } from '@progress/kendo-angular-inputs';
import { AssetMappingConfiguration, AssetTypeFilter, IModuleAllocation } from 'app/cem/project/models/asset-mapping-configuration';
import { GeneralProjectService } from 'app/cem/project/services/general-project.service';
import { AssetType, Project, ProjectAssetMapping } from 'app/shared/models';
import { debounceTime } from 'rxjs/operators';
import { ProjectModuleService } from '../../../services/project-module.service';

@Component({
  selector: 'app-project-asset-mapping-general-asset',
  templateUrl: './project-asset-mapping-general-asset.component.html',
  styleUrls: ['./project-asset-mapping-general-asset.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ComboBoxComponent,
    FormsModule,
    NumericTextBoxComponent,
    ReactiveFormsModule,
    TextBoxComponent,
  ],
})
export class ProjectAssetMappingGeneralAssetComponent implements OnInit {
  private projectModuleService = inject(ProjectModuleService);
  private generalProjectService = inject(GeneralProjectService);
  private errorService = inject(ErrorService);
  private ref = inject(ChangeDetectorRef);
  private formBuilder = inject(UntypedFormBuilder);

  private projectIdFilter = 'ProjectId eq';
  private superTypeFilter = 'Asset/AssetType/AssetSuperTypeId eq';
  private assetTypeFilter = 'Asset/AssetTypeId eq';
  private tableProjectAssetMapping: string = 'ProjectAssetMappings';
  private projectAssetMapId: number;
  private projectAssetMappings: ProjectAssetMapping[] = [];
  private assetSuperTypeId: number;
  private assetTypeFilters: AssetTypeFilter[] = [];
  private selectedAssetTypeId: number;
  private isConstructedAsset: boolean = false;
  private isSingleInstanceAsset: boolean = false;
  private tempProjectTypeSingleInstance: number;
  public moduleTitle: string;
  public moduleIsEditable: boolean = false;
  public hasAllocationPercentage: boolean = false;
  public allocationPercentage: number = 0;
  public isAssetSelected: boolean;
  public assetTypeList = new Array<AssetType>();
  public filteredAssetTypeList = new Array<AssetType>();
  public showToggleAllocation: boolean;
  public assetNameList = new Array<any>();
  public assetIdList = new Array<any>();
  public asset: any = {};
  public assetMapSearchForm: UntypedFormGroup;
  public assetMapForm: UntypedFormGroup;
  public showMultipleAssetMessage: boolean;
  public multipleAssetMessage: string = 'More than one asset is currently mapped to this project';
  public isInEditMode: boolean;
  public inactiveAssetsMessage: boolean = false;

  private _disabledAssetType: boolean = false;
  private _disabledAssets: boolean = true;
  public get disabledAssetType(): boolean { return this._disabledAssetType; }
  public set disabledAssetType(value: boolean) {
    this._disabledAssetType = value;
    if (this._disabledAssetType) {
      this.assetMapSearchForm.controls.AssetType.disable();
    } else {
      this.assetMapSearchForm.controls.AssetType.enable();
    }
  }
  public get disabledAssets(): boolean { return this._disabledAssets; }
  public set disabledAssets(value: boolean) {
    this._disabledAssets = value;
    if (this._disabledAssets) {
      this.assetMapSearchForm.controls.Asset.disable();
      this.assetMapForm.controls.AllocationPercentage.disable();
    } else {
      this.assetMapSearchForm.controls.Asset.enable();
      this.assetMapForm.controls.AllocationPercentage.enable();
    }
  }

  readonly projectId = input<number>(undefined);
  readonly project = input<Project>(undefined);
  readonly projectTypeId = input<number>(undefined);
  readonly assetTeamId = input<number>(undefined);
  readonly assetMappingConfig = input<AssetMappingConfiguration>(undefined);
  readonly projectAssetMappingService = input<ProjectAssetMappingService>(undefined);

  constructor() {
    this.generateForm();
    this.generateSearchForm();
  }

  public ngOnInit() {
    this.setValuesFromConfig();
    this.loadPageData();
    this.subscribeToProjectModuleEvents();

    // Disable PPID allocation for pad construction project
    if (this.projectTypeId() == ProjectTypeId.PadConstruction) {
      this.assetMapForm.get('AllocationPercentage')?.disable();
    }
  }

  private resetPage() {
    this.isInEditMode = false;
    this.isAssetSelected = false;
  }

  private loadPageData() {
    this.isInEditMode = false;
    this.isAssetSelected = false;
    this.assetNameList = [];
    this.assetIdList = [];

    this.loadAssetTypes();
    this.loadAssetMapping();
  }

  private generateForm() {
    this.assetMapForm = this.formBuilder.group({
      AllocationPercentage: [{ value: 0, disabled: this.disabledAssets }],
    });

    this.assetMapForm.valueChanges.pipe(debounceTime(500)).subscribe(data => {
      this.assetFormChange(data);
    });

    return this.assetMapForm;
  }

  private generateSearchForm() {
    this.assetMapSearchForm = this.formBuilder.group({
      Asset: [null, [Validators.required]],
      AssetType: [null, [Validators.required]]
    });

    this.assetMapSearchForm.valueChanges.subscribe(data => {
      this.assetMapSearchFormChange(data);
      this.projectAssetMappingService().tbAssetChanged.next(this.assetMapSearchForm);
    });
    return this.assetMapSearchForm;
  }

  private assetFormChange(data?: any) {
    if (this.assetMapForm && this.assetMapForm.dirty) {
      let allocationPercentage = 0;
      allocationPercentage = this.assetMapForm.controls.AllocationPercentage && this.assetMapForm.controls.AllocationPercentage.value;

      this.projectAssetMappingService().setModuleAllocation(
        {
          assetMappingConfigurationId: this.assetMappingConfig().AssetMappingConfigurationId,
          moduleId: this.assetMappingConfig().ModuleId,
          allocationPercentage: allocationPercentage || 0,
          hasAssets: true,
          isActiveToggle: true,
          isToggle: this.assetMappingConfig().ModuleProperties.IsToggleModule,
          hasPendingChange: true
        }
      );
    }
  }

  private assetMapSearchFormChange(data?: any) {
    if (this.assetMapSearchForm.valid && this.assetMapSearchForm.dirty) {
      let allocationPercentage = 0;
      if (this.assetMapForm)
        allocationPercentage = this.assetMapForm.controls.AllocationPercentage && this.assetMapForm.controls.AllocationPercentage.value;

      this.projectAssetMappingService().setModuleAllocation(
        {
          assetMappingConfigurationId: this.assetMappingConfig().AssetMappingConfigurationId,
          moduleId: this.assetMappingConfig().ModuleId,
          allocationPercentage: allocationPercentage || 0,
          hasAssets: true,
          isActiveToggle: true,
          isToggle: this.assetMappingConfig().ModuleProperties.IsToggleModule,
          hasPendingChange: true
        }
      );
    }
  }

  private formatfilterString(): string {
    if (this.assetTypeFilters.length > 0) {
      let constructedFilter = '';
      this.assetTypeFilters.map((filter, i) => {
        let assetTypeFilter = `(${this.projectIdFilter} ${this.projectId()} and ${this.assetTypeFilter} ${filter.AssetTypeId})`;
        constructedFilter = constructedFilter + assetTypeFilter;
        if (this.assetTypeFilters.length - 1 !== i) {
          constructedFilter = constructedFilter + ' or ';
        }
      });
      return constructedFilter;
    }
    return `${this.projectIdFilter} ${this.projectId()} and ${this.superTypeFilter} ${this.assetSuperTypeId}`;
  }

  private loadAssetTypes() {
    this.generalProjectService.getAssetTypes()
      .then(x => {
        this.assetTypeList = x;
        this.setAssetTypeDropDownList();
      });
  }

  private loadAssetMapping() {
    this.generalProjectService.getProjectAssetMappingBySuperType(this.formatfilterString())
      .then(x => {
        this.projectAssetMappings = x;
        this.project().ProjectAssetMappings_Project = x;
        this.setAssetFormValues()
      });
  }

  private loadMappedAsset(assetId: number) {
    this.inactiveAssetsMessage = false;
    this.generalProjectService.getAssetMapped(assetId)
      .then(x => {
        let asset = x[0];
        this.assetMapSearchForm.setValue({
          Asset: asset,
          AssetType: { AssetTypeId: asset.AssetTypeId, Description: asset.AssetType }
        });
        this.asset = asset;
        this.assetNameList.push(asset);
        this.assetIdList.push(asset);
        if (!this.asset.IsActive)
          this.inactiveAssetsMessage = true;
      });
  }

  private subscribeToProjectModuleEvents() {
    this.projectAssetMappingService().getModulesToReload()
      .subscribe((event) => {
        event.map((data) => {
          const assetMappingConfig = this.assetMappingConfig();
          if (data && assetMappingConfig.AssetMappingConfigurationId && data.assetMappingConfigurationId === assetMappingConfig.AssetMappingConfigurationId) {
            this.clearAllocation(data);
          }
        });
      });

    this.projectAssetMappingService().getModulesToSaveChanges()
      .subscribe((event) => {
        event.map((data) => {
          const assetMappingConfig = this.assetMappingConfig();
          if (data && assetMappingConfig.AssetMappingConfigurationId && data.assetMappingConfigurationId === assetMappingConfig.AssetMappingConfigurationId) {
            this.updateProjectAssetMapping();
          }
        });
      });

    this.projectAssetMappingService().getModulesToCancelChanges()
      .subscribe((event) => {
        event.map((data) => {
          const assetMappingConfig = this.assetMappingConfig();
          if (data && assetMappingConfig.AssetMappingConfigurationId && data.assetMappingConfigurationId === assetMappingConfig.AssetMappingConfigurationId) {
            this.cancelChanges();
          }
        });
      });
  }

  private clearAllocation(module: IModuleAllocation) {
    this.showToggleAllocation = true;
    this.asset = {};
    this.projectAssetMappingService().setModuleAllocation(module);
  }

  private setAssetFormValues() {
    if (this.projectAssetMappings.length > 1) {
      this.showMultipleAssetMessage = true;
    } else {
      let allocationPercentage = this.projectAssetMappings[0] && this.projectAssetMappings[0].PercentAllocation;
      this.projectAssetMapId = this.projectAssetMappings[0] && this.projectAssetMappings[0].ProjectAssetMappingId;
      this.setToggleModule(this.projectAssetMapId ? true : false);

      this.projectAssetMappingService().setModuleAllocation(
        {
          assetMappingConfigurationId: this.assetMappingConfig().AssetMappingConfigurationId,
          moduleId: this.assetMappingConfig().ModuleId,
          allocationPercentage: allocationPercentage || 0,
          hasAssets: this.projectAssetMapId ? true : false,
          isActiveToggle: this.projectAssetMapId ? true : false,
          isToggle: this.assetMappingConfig().ModuleProperties.IsToggleModule,
          hasPendingChange: false
        }
      );

      if (this.projectAssetMappings !== null && this.projectAssetMappings[0] && this.projectAssetMappings[0].AssetId !== 0) {
        this.allocationPercentage = allocationPercentage || 0;
        this.loadMappedAsset(this.projectAssetMappings[0].AssetId);
        this.assetMapForm.patchValue({
          AllocationPercentage: this.allocationPercentage
        });
      }
    }
  }

  private setToggleModule(hasAsset: boolean) {
    if (this.assetMappingConfig().ModuleProperties.IsToggleModule) {
      this.showToggleAllocation = !hasAsset;
    }
  }

  private setValuesFromConfig() {
    let config = this.assetMappingConfig();

    if (config) {
      if (config.AssetTypeFilters) {
        this.assetTypeFilters = config.AssetTypeFilters;
      }
      this.isConstructedAsset = config.IsConstructedModule && config.IsConstructedModule;
      this.isSingleInstanceAsset = config.IsSingleInstanceAsset && config.IsSingleInstanceAsset;
      this.tempProjectTypeSingleInstance = this.isSingleInstanceAsset ? this.projectTypeId() : null;
      this.assetSuperTypeId = config.AssetSuperTypeId;
      this.moduleIsEditable = config.ModuleProperties.IsMappingEditable && config.ModuleProperties.IsMappingEditable;
      this.disabledAssets = !this.moduleIsEditable;
    }
  }

  private setAssetTypeDropDownList() {
    if (this.assetTypeFilters.length > 0) {
      this.filteredAssetTypeList = [];
      for (let item of this.assetTypeFilters) {
        let assetType = this.assetTypeList.find(a => a.AssetTypeId === item.AssetTypeId);
        this.filteredAssetTypeList.push(assetType);
      }
    }
    if (this.assetTypeFilters.length === 1) {
      let assetType = this.assetTypeList.find(a => a.AssetTypeId === this.assetTypeFilters[0].AssetTypeId);
      this.disabledAssetType = true;
      this.selectedAssetTypeId = assetType.AssetTypeId;
      this.assetMapSearchForm.patchValue({
        AssetType: {
          AssetTypeId: assetType.AssetTypeId,
          Description: assetType.Description
        }
      });
    }
  }

  public toggleAllocationModule() {
    this.showToggleAllocation = false;
    this.projectAssetMappingService().toggleModuleAllocation(this.projectId(), this.assetMappingConfig().AssetMappingConfigurationId);
  }

  public handleAssetTypeSelectionChange(value) {
    if (value === undefined) {
      this.disabledAssets = true;
      this.selectedAssetTypeId = null;
    } else {
      this.selectedAssetTypeId = value.AssetTypeId;
      this.disabledAssets = false;
    }
  }

  public handelAssetNameSelectionChange(value) {
    if (value) {
      this.isAssetSelected = true;
      this.asset = Object.assign({}, value);
      this.assetIdList = [this.asset];
      this.assetNameList = [this.asset];
    } else {
      this.isAssetSelected = false;
    }
  }

  public handelAssetIdSelectionChange(value) {
    if (value) {
      this.isAssetSelected = true;
      this.asset = Object.assign({}, value);
      this.assetIdList = [this.asset];
      this.assetNameList = [this.asset];
    } else {
      this.isAssetSelected = false;
    }
  }

  public handleAssetFilter(value) {
    if (value.length <= 2) {
      return this.assetNameList = [];
    }

    this.generalProjectService.getAssets(this.assetTeamId(), this.selectedAssetTypeId, value, 'AssetName', this.tempProjectTypeSingleInstance, this.project())
      .then(x => {
        this.assetNameList = x;
        this.ref.markForCheck();
      });
  }

  public handleAssetIdFilter(value) {
    if (value.length <= 2) {
      return this.assetIdList = [];
    }

    this.generalProjectService.getAssets(this.assetTeamId(), this.selectedAssetTypeId, value, 'AssetId', this.tempProjectTypeSingleInstance, this.project())
      .then(x => {
        this.assetIdList = x;
        this.ref.markForCheck();
      });
  }

  public cancelChanges() {
    this.loadPageData();
    if (this.assetMapSearchForm) this.assetMapSearchForm.reset();
    if (this.assetMapForm) this.assetMapForm.reset();
    this.isInEditMode = false;
  }

  public updateProjectAssetMapping() {
    let allocationPercentage = 0;
    this.isInEditMode = false;
    allocationPercentage = this.assetMapForm.controls.AllocationPercentage && this.assetMapForm.controls.AllocationPercentage.value;

    if (this.assetMapSearchForm.controls.Asset.value == null) {
      return;
    }

    if (this.projectAssetMapId === 0 || !this.projectAssetMapId) {
      this.addAssetMapping(allocationPercentage);
    }
    else {
      this.updateAssetMapping(allocationPercentage);
    }
  }

  private addAssetMapping(allocationPercentage: number) {
    let newMapping: any = {
      ProjectId: this.projectId(),
      AssetId: this.assetMapSearchForm.controls.Asset.value.AssetId,
      IsConstructedAsset: this.isConstructedAsset,
      PercentAllocation: allocationPercentage
    };
    let form = JSON.stringify(newMapping);

    this.projectModuleService.create(form, this.tableProjectAssetMapping)
      .then(() => {
        this.resetPage();
        if (this.assetMapSearchForm) this.assetMapSearchForm.reset();
        if (this.assetMapForm) this.assetMapForm.reset();
        this.loadAssetMapping();
        this.projectAssetMappingService().tbAssetSaved.next(true);
      })
      .catch((error) => {
        this.errorService.error('Error creating asset mappping for asset', error);
        this.resetPage();
      });
  }

  private updateAssetMapping(allocationPercentage: number) {
    let assetId = this.assetMapSearchForm.controls.Asset.value && this.assetMapSearchForm.controls.Asset.value.AssetId;
    if (this.assetMapSearchForm) this.assetMapSearchForm.reset();
    if (this.assetMapForm) this.assetMapForm.reset();
    let updateMapping: any = { AssetId: assetId, ProjectId: this.projectId(), PercentAllocation: allocationPercentage };
    let form = JSON.stringify(updateMapping);

    this.projectModuleService.update(this.projectAssetMapId, form, this.tableProjectAssetMapping)
      .then(() => {
        this.loadAssetMapping()
        this.projectAssetMappingService().tbAssetSaved.next(true);
      })
      .catch((error) => {
        this.errorService.error('Error updating asset mappping for asset', error);
        this.resetPage();
      });
  }
}